/**
 * Canonical value→string serialization for error logging. Apps contribute
 * domain-specific formatting (e.g. HTTP response shapes) via
 * {@link registerErrorRecognizer} and sensitive-data removal via
 * {@link registerLogScrubber}; everything else falls through to built-ins that
 * always produce an informative string — never "[object Object]".
 *
 * Recognizer dispatch scope: recognizers only ever see the top-level value
 * passed to {@link serializeError} and any value a recognizer's own `format`
 * explicitly hands back to the provided `serialize` callback. Values nested
 * inside a plain object/array that reach {@link toJsonString} are formatted
 * leaf-by-leaf via `JSON.stringify`'s replacer (`serializeInner`) — that
 * recursion does not re-run recognizer `test`s against intermediate
 * containers, only against each JSON leaf. This matches the legacy
 * (pre-canonical-pipeline) formatting behavior.
 */

export type ErrorRecognizer = {
  /** Diagnostic name, e.g. 'axios-response'. */
  name: string;
  test: (value: unknown) => boolean;
  /** May recurse into nested values via the provided serialize callback. */
  format: (value: unknown, serialize: (v: unknown) => string) => string;
};

export type LogScrubber = (text: string) => string;

const recognizers: ErrorRecognizer[] = [];
const scrubbers: LogScrubber[] = [];

/** Register an app-specific recognizer; earlier registrations win. Returns an unregister fn. */
export function registerErrorRecognizer(recognizer: ErrorRecognizer): () => void {
  recognizers.push(recognizer);
  return () => {
    const index = recognizers.indexOf(recognizer);
    if (index >= 0) recognizers.splice(index, 1);
  };
}

/** Register a scrubber applied to every serialized string. Returns an unregister fn. */
export function registerLogScrubber(scrubber: LogScrubber): () => void {
  scrubbers.push(scrubber);
  return () => {
    const index = scrubbers.indexOf(scrubber);
    if (index >= 0) scrubbers.splice(index, 1);
  };
}

/** Visible for testing only. */
export function resetErrorSerializationForTesting(): void {
  recognizers.length = 0;
  scrubbers.length = 0;
}

function isPlainObjectLike(value: object): boolean {
  const proto = Object.getPrototypeOf(value);
  if (proto === null || proto === Object.prototype) {
    return true;
  }
  // Cross-realm plain objects: one-level chain ending at a foreign Object.prototype
  return Object.getPrototypeOf(proto) === null && value.constructor?.name === 'Object';
}

function isErrorLike(value: unknown): value is { name?: string; message: string; stack?: string } {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  if (Object.prototype.toString.call(value) === '[object Error]') {
    return true;
  }
  // The tag check above only matches objects carrying the [[ErrorData]] internal
  // slot. Error-shaped objects without it (Object.create(Error.prototype)
  // hydrations, custom error classes that don't extend Error) need this duck-type
  // fallback — mirroring lodash's isError, which the legacy formatValue relied on.
  const maybe = value as { name?: unknown; message?: unknown };
  return typeof maybe.name === 'string' && typeof maybe.message === 'string' && !isPlainObjectLike(value);
}

function formatErrorLike(value: { name?: string; message?: string; stack?: string; toString?: () => string }): string {
  const stack = value.stack;
  if (!stack) {
    return String(value);
  }
  // V8 (Chrome/Edge/Node) stacks embed "Name: message" as their first line, but
  // SpiderMonkey (Firefox) and JavaScriptCore (Safari) stacks contain only the
  // frames ("fn@file:line:col"), which would silently drop the error message from
  // the log body. Detect which shape we have from the content itself rather than
  // UA-sniffing so the guarantee holds engine-independently.
  // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/stack
  if (stack.startsWith(value.name ?? 'Error')) {
    return stack;
  }
  return [String(value), ...stack.split('\n')].join('\n  ');
}

function toJsonString(value: object): string {
  // Track only the current recursion *path* (ancestors), not every visited
  // container — a DAG can legitimately reference the same sub-object from two
  // siblings, and only a true cycle (a container that is its own ancestor)
  // should be reported as [Circular]. JSON.stringify invokes the replacer with
  // `this` bound to the holder (the containing object/array), so a regular
  // function can use that to detect when recursion has returned to an
  // ancestor and pop the stack back to that point.
  const ancestors: object[] = [];
  return JSON.stringify(value, function (replacerKey, replacerValue) {
    const isContainer =
      Array.isArray(replacerValue) ||
      (typeof replacerValue === 'object' && replacerValue !== null && isPlainObjectLike(replacerValue));
    if (isContainer) {
      while (ancestors.length > 0 && ancestors[ancestors.length - 1] !== this) {
        ancestors.pop();
      }
      if (ancestors.includes(replacerValue)) {
        return '[Circular]';
      }
      ancestors.push(replacerValue);
      return replacerValue;
    }
    return serializeInner(replacerValue);
  });
}

function serializeInner(value: unknown): string {
  for (const recognizer of recognizers) {
    if (recognizer.test(value)) {
      return recognizer.format(value, serializeInner);
    }
  }
  if (typeof value === 'string') {
    return value;
  }
  if (value === null || value === undefined) {
    return String(value);
  }
  if (isErrorLike(value)) {
    return formatErrorLike(value);
  }
  if (Array.isArray(value) || (typeof value === 'object' && isPlainObjectLike(value))) {
    return toJsonString(value as object);
  }
  if (typeof value === 'function') {
    return `[Function] ${value.name || '(anonymous)'}`;
  }
  if (typeof value === 'object') {
    const type = value.constructor?.name || 'anonymous';
    // Deliberate fallback for exotic objects (e.g. Promise): the default
    // Object.prototype.toString ("[object X]") is exactly what we want here,
    // prefixed with the constructor name so it's never the bare "[object Object]".
    // oxlint-disable-next-line @typescript-eslint/no-base-to-string
    return `[${type}] ${value.toString()}`;
  }
  if (Object.is(value, -0)) {
    return '-0';
  }
  // oxlint-disable-next-line @typescript-eslint/no-base-to-string
  return String(value);
}

/**
 * Serialize any value for an error/log record body. Registered recognizers run
 * first; built-ins guarantee a useful fallback. The final string passes through
 * all registered scrubbers. Never throws.
 */
export function serializeError(value: unknown): string {
  try {
    return scrubbers.reduce((memo, scrubber) => scrubber(memo), serializeInner(value));
  } catch (ex) {
    return `[${String(ex)} when formatting]`;
  }
}
